import { SyntheticEvent, useEffect, useState } from 'react';

export type TransitionState = 'mounting' | 'entering' | 'exiting' | 'exited';

/** Duration of the CSS transition in seconds. */
export const transitionDuration = 0.2;

/** Delay before certain CSS transitions start, in seconds. */
export const transitionDelay = 0.05;

/**
 * Max time (ms) an exit transition can take before we force the state to
 * 'exited'. Derived from the CSS timing values with a comfortable margin.
 */
const EXIT_TIMEOUT_MS = (transitionDuration + transitionDelay) * 1000 + 50;

export const useTransitionState = (
  isOpen: boolean
): [TransitionState, (e?: SyntheticEvent) => void] => {
  const [state, setState] = useState<TransitionState>('exited');
  const currentState = getCurrentState(state, isOpen);

  /**
   * Safety net: if the element stays in 'exiting' longer than the CSS
   * transition can possibly last, force it to 'exited'.  This handles
   * edge-cases where transitionend/animationend events are swallowed
   * (e.g. when another modal's portal mount triggers a DOM mutation that
   * cancels the running CSS transition).
   */
  useEffect(() => {
    if (currentState !== 'exiting') return;

    const timer = setTimeout(() => setState('exited'), EXIT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [currentState]);

  const updateState = (e?: SyntheticEvent) => {
    // Skip bubbled events from child elements
    if (e && e.target !== e.currentTarget) {
      return;
    }
    setState(getCurrentState(currentState, isOpen));
  };

  return [currentState, updateState];
};

const getCurrentState = (
  state: TransitionState,
  open: boolean
): TransitionState => {
  if (open && state === 'exited') {
    return 'mounting';
  }
  if (open && state === 'mounting') {
    return 'entering';
  }
  if (!open && state === 'entering') {
    return 'exiting';
  }
  if (!open && state === 'exiting') {
    return 'exited';
  }
  // Handle case where modal is closed before mounting animation completes
  if (!open && state === 'mounting') {
    return 'exited';
  }

  return state;
};
